// Typed contracts for the scoring submit → poll → result flow (Story 9.7).
//
// These mirror the EXACT backend shapes:
//  - submit 202  → src/application/evaluation/submit_evaluation/schemas.py::SubmitResponse
//  - status poll → src/application/scoring/get_scoring_job_status/schemas.py
//  - result read → src/application/scoring/get_scoring_result/schemas.py
// All values arrive already envelope-unwrapped by apiFetch (you receive `data`).
//
// Decimal DISCIPLINE: every measure (`*_logit`, `std_*`, `percentile_*`) arrives as an exact
// decimal STRING (Pydantic v2 serializes Decimal as a string, e.g. "1.25"), never a JS number.
// Typed `string` so a consumer must format it deliberately and can NEVER `float()`-round a
// clinical measure (CLAUDE.md Rule 2). The out-of-normative-range marker is the NULL
// std/percentile triple (`null`), never `0`.

import type { EvaluationState } from "@/hooks/queries/evaluation-types";

/** The full scoring-job lifecycle enum (mirror of `SCORING_JOB_STATUSES`). */
export type ScoringJobStatusValue =
  | "queued"
  | "processing"
  | "completed"
  | "failed"
  | "blocked";

/** Terminal statuses end the polling loop. */
export const TERMINAL_STATUSES: ReadonlySet<ScoringJobStatusValue> = new Set([
  "completed",
  "failed",
  "blocked",
]);

export function isTerminalStatus(
  status: ScoringJobStatusValue | undefined,
): boolean {
  return status !== undefined && TERMINAL_STATUSES.has(status);
}

/**
 * Polling contract (from the 9.5 endpoint docstring — the source of truth): poll every
 * 2 s with a 5-minute overall timeout. Module constants so the hook AND its tests read one
 * literal (no drift). This is the repo's FIRST `refetchInterval` usage — keep it exemplary.
 */
export const POLL_INTERVAL_MS = 2000;
export const POLL_TIMEOUT_MS = 300_000;

/** Envelope-unwrapped body of `POST /evaluations/{id}/submit` (202 ACCEPTED). */
export interface EvaluationSubmitResponse {
  id: string;
  version: number;
  updated_at: string | null;
  state: EvaluationState;
  /**
   * OPAQUE scoring-job handle. **Opacity fence (9.5):** store this value from the 202 and
   * replay it verbatim to the status poll — NEVER reconstruct the poll URL from an
   * `evaluation_id`. `scoring_job_id === evaluation_id` is an internal 9.5 detail today, not
   * a contract; a future FR37 re-score would break any client that re-derives it.
   */
  scoring_job_id: string;
}

/** Envelope-unwrapped body of `GET /scoring-jobs/{scoring_job_id}/status`. */
export interface ScoringJobStatus {
  scoring_job_id: string;
  status: ScoringJobStatusValue;
}

/**
 * Envelope-unwrapped body of `GET /scoring-jobs/by-evaluation/{evaluation_id}/status`
 * (gh-586) — mirror of `EvaluationScoringStatusResponse`
 * (src/application/scoring/get_scoring_job_status/schemas.py).
 *
 * The fence-preserving status read for a results route with NO stored opaque job handle:
 * the client polls by the `evaluation_id` it owns and the server resolves the job. The
 * body deliberately carries `evaluation_id` and **no** `scoring_job_id` — the client never
 * receives a job handle, so the Story 9.7 opacity fence holds.
 */
export interface EvaluationScoringStatus {
  evaluation_id: string;
  status: ScoringJobStatusValue;
}

/**
 * Envelope-unwrapped body of `GET /scoring-results/{evaluation_id}` (Story 9.7 read slice).
 *
 * z-scores and interpretive ranges are DELIBERATELY absent (Spencer sign-off, 2026-07-13):
 * the report is logit + standard score + percentile only. `std_*` / `percentile_*` are `null`
 * for an out-of-normative-range client (the panel renders "Outside normative range").
 */
export interface ScoringResult {
  evaluation_id: string;
  client_id: string;
  motor_logit: string;
  process_logit: string;
  std_motor: string | null;
  std_process: string | null;
  percentile_motor: string | null;
  percentile_process: string | null;
  reference_data_version: string;
  calibration_version: string;
  formula_version: string;
  created_at: string;
}

/**
 * One row of `GET /scoring-results` (Story 9.14 list slice). Standard scores ONLY —
 * logits/percentiles stay behind the 9.7 single-result read; the client NAME rides
 * the 5.4 reveal flow. `std_*` are exact decimal strings, `null` = out of normative
 * range (render an em dash, never 0). `evaluation_id` is the navigation key into
 * the Results panel (`/evaluations/{evaluation_id}`), never a scoring_job_id.
 */
export interface ScoringResultListItem {
  id: string;
  evaluation_id: string;
  client_id: string;
  std_motor: string | null;
  std_process: string | null;
  created_at: string;
}

/** Envelope-unwrapped body of `GET /scoring-results` (a `PaginatedData<ScoringResultListItem>`). */
export interface ScoringResultsListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ScoringResultListItem[];
}
